export const DEFAULT_PAGE_SIZE = 12;
export const DEFAULT_TABLE_PAGE_SIZE = 10;
export const MIN_PAGE_SIZE = 1;
export const MAX_PAGE_SIZE = 100;

export function formatVersion(version: number | string | null | undefined): string {
  if (version === null || version === undefined || version === "") return "-";
  const str = String(version).trim();
  if (!str || str === "-" || str === "—") return "-";
  return /^v/i.test(str) ? `V${str.slice(1)}` : `V${str}`;
}

/**
 * Turn a visible UI label into a DOM id, so a widget's id always matches the
 * title the user sees. Lowercased, non-alphanumerics collapsed to hyphens.
 *
 * Examples: "Findings & POA&M Status" → "findings-poam-status",
 *           "SSP Coverage" → "ssp-coverage".
 */
export function labelToDomId(label: string, suffix = ""): string {
  const slug = label
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return suffix ? `${slug}-${suffix}` : slug;
}

/** Max length of an SSP short code (matches the backend's cap). */
export const SSP_SHORT_CODE_MAX = 10;

/**
 * Normalize an SSP short code the way the backend stores it: uppercased, only
 * ASCII letters/digits (spaces, hyphens, symbols, accented/unicode letters all
 * removed), capped at SSP_SHORT_CODE_MAX chars. Safe for any input —
 * null/undefined/non-string yield "".
 *
 * Examples: "  pay-1 " → "PAY1", "café" → "CAF", "a_b c!" → "ABC".
 */
export function sanitizeSspShortCode(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, SSP_SHORT_CODE_MAX);
}

/**
 * Normalizes a date-like input to a valid Date object, or null if invalid/empty.
 * Handles:
 * - Date instances
 * - Unix epoch timestamps in seconds (< 1e11) or milliseconds (>= 1e11)
 * - Numeric string timestamps (e.g. "1793263308")
 * - 0 or negative timestamps (treated as empty/unset -> null)
 * - DD/MM/YYYY or DD-MM-YYYY strings (avoiding MM/DD parsing flips)
 * - ISO date/datetime strings
 */
export function toSafeDate(
  value: string | number | Date | null | undefined,
): Date | null {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "number") {
    if (value <= 0 || isNaN(value)) return null;
    const ms = value < 1e11 ? value * 1000 : value;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "-" || trimmed === "—") return null;

    // Pure numeric string: epoch seconds or milliseconds
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      if (num <= 0 || isNaN(num)) return null;
      const ms = num < 1e11 ? num * 1000 : num;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }

    // DD/MM/YYYY or DD-MM-YYYY (with optional time)
    const ddmmyyyy =
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[T\s](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?$/.exec(
        trimmed,
      );
    if (ddmmyyyy) {
      const day = parseInt(ddmmyyyy[1], 10);
      const month = parseInt(ddmmyyyy[2], 10) - 1;
      const year = parseInt(ddmmyyyy[3], 10);
      const hour = ddmmyyyy[4] ? parseInt(ddmmyyyy[4], 10) : 0;
      const min = ddmmyyyy[5] ? parseInt(ddmmyyyy[5], 10) : 0;
      const sec = ddmmyyyy[6] ? parseInt(ddmmyyyy[6], 10) : 0;
      const d = new Date(year, month, day, hour, min, sec);
      return isNaN(d.getTime()) ? null : d;
    }

    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Safely formats a date-like input. Returns `fallback` (default "-") for
 * null, undefined, empty strings, or unparseable dates — never "Invalid Date".
 * Locale defaults to "en-GB" (DD/MM/YYYY) to match existing card conventions.
 */
export function safeFormatDate(
  value: string | number | Date | null | undefined,
  locale: string = "en-GB",
  fallback: string = "-",
): string {
  const d = toSafeDate(value);
  if (!d) return fallback;
  try {
    return d.toLocaleDateString(locale);
  } catch {
    return fallback;
  }
}

export function todayISO(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Formats a date-like input as `DD/MM/YYYY, hh:mm AM/PM` (e.g. "25/02/2026, 08:52 PM").
 * Returns `fallback` (default "-") for null, undefined, empty, or unparseable dates.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback: string = "-",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed) || /^\d{1,2}[/-]\d{1,2}[/-]\d{4}$/.test(trimmed)) {
      return safeFormatDate(trimmed, "en-GB", fallback);
    }
  }
  const d = toSafeDate(value);
  if (!d) return fallback;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const rawHours = d.getHours();
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const hour12 = String(rawHours % 12 || 12).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year}, ${hour12}:${minutes} ${ampm}`;
}

/** Already in the target shape — with or without the time half. */
const DDMMYYYY_HHMM = /^\d{2}\/\d{2}\/\d{4}(, \d{2}:\d{2})?$/;

/**
 * Formats a date-like input as `DD/MM/YYYY, HH:mm` on a 24-hour clock
 * (e.g. "12/06/2026, 03:15"). This is the shape the IT Compliance / CISO
 * Insights backends already return for their pre-formatted date columns, so
 * ISO timestamps rendered beside them (`lastSeenAt`, `fetchAt`) match rather
 * than leaking "2026-06-24T00:00" into the UI. Distinct from `formatDateTime`
 * above, which is the app's 12-hour "08:52 PM" convention.
 *
 * Values already in that shape are returned verbatim. That check is load-
 * bearing, not an optimisation: `new Date("12/06/2026")` reads DD/MM as MM/DD
 * and would silently move 12 June to 6 December. It also keeps a date-only
 * backend string intact instead of failing to parse it down to the fallback.
 */
export function formatDateTime24(
  value: string | number | Date | null | undefined,
  fallback: string = "—",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "—" || trimmed === "-") return fallback;
    if (DDMMYYYY_HHMM.test(trimmed)) return trimmed;
  }
  const d = toSafeDate(value);
  if (!d) return fallback;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO `YYYY-MM-DD` -> a local-midnight `Date` for the `DatePicker`. Parsed
 * field-by-field rather than via `new Date(iso)`, which reads a bare date
 * string as UTC and can land on the previous day west of Greenwich. */
export function isoToDate(iso: string | null | undefined): Date | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return undefined;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? undefined : d;
}

/** A `DatePicker` `Date` -> ISO `YYYY-MM-DD`, using local calendar fields so the
 * day the user clicked is the day that gets submitted (see `isoToDate`). */
export function dateToIso(d: Date | undefined): string {
  if (!d || isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight today — the `before` bound for target-date pickers. */
export function startOfTodayLocal(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
const MAX_JSON_UNWRAPS = 3;

const MAX_EVIDENCE_DEPTH = 12;

function parseJsonDeep(text: string): unknown {
  let current: unknown = text;
  for (let pass = 0; pass < MAX_JSON_UNWRAPS; pass += 1) {
    if (typeof current !== "string") return current;
    const trimmed = current.trim();
    const first = trimmed[0];
    if (first !== "{" && first !== "[" && first !== '"') {
      return pass === 0 ? undefined : current;
    }
    try {
      current = JSON.parse(trimmed);
    } catch {
      return pass === 0 ? undefined : current;
    }
  }
  return current;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function decodeValue(value: unknown, depth: number): unknown {
  if (depth >= MAX_EVIDENCE_DEPTH) return value;
  if (typeof value === "string") {
    const parsed = parseJsonDeep(value);
    if (parsed !== undefined && typeof parsed === "object" && parsed !== null) {
      return decodeValue(parsed, depth + 1);
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((entry) => decodeValue(entry, depth + 1));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = decodeValue(entry, depth + 1);
    }
    return out;
  }
  return value;
}

export function decodeEvidence(raw: unknown): unknown {
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "object") return decodeValue(raw, 0);
  if (typeof raw !== "string") return raw;
  if (raw.trim() === "") return undefined;
  const parsed = parseJsonDeep(raw);
  if (parsed === undefined) return undefined;
  return decodeValue(parsed, 0);
}

export function formatEvidenceJson(raw: unknown): string | null {
  const decoded = decodeEvidence(raw);
  if (decoded === undefined) return null;
  try {
    return JSON.stringify(decoded, null, 2);
  } catch {
    return null;
  }
}

/**
 * "CN=Admins,OU=Groups,DC=corp" -> "Admins".
 * Safe on missing or malformed values - returns "" instead of throwing.
 */
export function shortGroupName(dnOrName?: string | null): string {
  return dnOrName?.split(",")[0]?.replace(/^[Cc][Nn]=/, "") ?? "";
}

/**
 * Format a lifecycle or workflow status for display, matching the application's
 * standard status badge capitalization (e.g. "in_review" -> "In Review",
 * "approved" -> "Approved", "published" -> "Published").
 */
export function formatStatusLabel(status: string | null | undefined): string {
  if (!status) return "";
  const s = status.trim();
  if (s === "retired") return "Withdrawn";
  return s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
